import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Bell, Mail, Smartphone, Loader2, CheckCircle2 } from 'lucide-react';

const timeAgo = (iso) => {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const PREF_META = {
  email: { label: 'Email', Icon: Mail },
  push: { label: 'Push', Icon: Smartphone },
  both: { label: 'Email + Push', Icon: Bell },
};

const PrefBadge = ({ pref }) => {
  const meta = PREF_META[pref] || PREF_META.email;
  const { Icon, label } = meta;
  return (
    <Badge variant="outline" className="gap-1 shrink-0 text-muted-foreground" data-testid={`notify-pref-${pref}`}>
      <Icon className="h-3 w-3" /> {label}
    </Badge>
  );
};

const resolveTemplate = (str, vars) => {
  let result = str || '';
  Object.entries(vars).forEach(([k, v]) => {
    if (k === 'operator_name') return; // resolved per-worker on the backend
    result = result.replace(new RegExp(`\\{${k}\\}`, 'g'), v || '');
  });
  return result;
};

const buildVariables = (job) => {
  if (!job) return {};
  return {
    job_number: job.job_number || job.id?.slice(-6) || '',
    site_name: job.site_name || job.location || '',
    customer_name: job.customer_name || job.client || '',
    company_name: job.customer_name || '',
    date: job.start_date || '',
    start_time: job.start_time || '',
    depot_time: job.depot_time || '',
    description: job.description || '',
    equipment_name: job.equipment_type || '',
  };
};

/**
 * Unified "Notify Workers" dialog.
 * Sends job details to selected allocated workers via each worker's preferred
 * delivery method (Email / Push / Both). Push recipients get the (editable,
 * template-based) message; email recipients get the standard job-details email.
 */
export const NotifyWorkersDialog = ({ open, onOpenChange, api, jobId, job, jobLabel = '', onSent }) => {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [selected, setSelected] = useState({});
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [pushTitle, setPushTitle] = useState('');
  const [pushMessage, setPushMessage] = useState('');

  const anyPush = recipients.some((r) => selected[r.operator_id] && (r.notification_preference === 'push' || r.notification_preference === 'both'));

  const load = () => {
    if (!jobId) return;
    setLoading(true);
    api.get(`/jobs/${jobId}/notify-recipients`)
      .then((res) => {
        const recs = res.data?.recipients || [];
        setRecipients(recs);
        const preset = {};
        recs.forEach((r) => { preset[r.operator_id] = !r.last_emailed_at; });
        setSelected(preset);
      })
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load workers'))
      .finally(() => setLoading(false));

    // Load push templates (best-effort — falls back to a default message).
    api.get('/notification-templates')
      .then((res) => {
        const tmpls = (res.data?.templates || []).filter((t) => t.is_active);
        setTemplates(tmpls);
        const preferred = tmpls.find((t) => t.notification_type === 'job_assigned') || tmpls[0];
        if (preferred) applyTemplate(preferred);
      })
      .catch(() => {
        // No template permission — seed a sensible default.
        const vars = buildVariables(job);
        setPushTitle('New job assigned');
        setPushMessage(resolveTemplate('Job #{job_number} at {site_name} on {date}, start {start_time}. Tap for details.', vars));
      });
  };

  const applyTemplate = (t) => {
    setTemplateId(t.id);
    const vars = buildVariables(job);
    setPushTitle(resolveTemplate(t.title_template, vars));
    setPushMessage(resolveTemplate(t.message_template, vars));
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, jobId]);

  const chosen = recipients.filter((r) => selected[r.operator_id]);

  const handleSend = async () => {
    if (chosen.length === 0) { toast.error('Select at least one worker'); return; }
    setSending(true);
    try {
      const res = await api.post(`/jobs/${jobId}/notify-workers`, {
        operator_ids: chosen.map((r) => r.operator_id),
        push_title: pushTitle,
        push_message: pushMessage,
      });
      const notified = res.data?.notified || [];
      const skipped = res.data?.skipped_no_email || [];
      const failed = res.data?.failed || [];
      if (notified.length) {
        toast.success(`Notified ${notified.length} worker(s): ${notified.map((n) => n.name).join(', ')}`);
      }
      if (skipped.length) toast.warning(`No email address: ${skipped.join(', ')}`);
      if (failed.length) toast.error(`Failed to notify: ${failed.join(', ')}. Check email settings.`);
      if (!notified.length && !skipped.length && !failed.length) toast.info('Nothing to send.');
      load();
      if (onSent) onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to notify workers');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="notify-workers-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-[#ffd108]" />
            Notify Workers
          </DialogTitle>
          <DialogDescription>
            {jobLabel ? `Send ${jobLabel} details to workers via their preferred method.` : 'Send job details to workers via their preferred method.'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : recipients.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No workers are allocated to this job yet.</p>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {recipients.map((r) => (
                <label
                  key={r.operator_id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${selected[r.operator_id] ? 'border-[#ffd108]/60 bg-[#ffd108]/5' : 'border-border'}`}
                  data-testid={`notify-recipient-${r.operator_id}`}
                >
                  <Checkbox
                    checked={!!selected[r.operator_id]}
                    onCheckedChange={(v) => setSelected((prev) => ({ ...prev, [r.operator_id]: !!v }))}
                    data-testid={`notify-recipient-check-${r.operator_id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{r.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{r.email || 'No email address'}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <PrefBadge pref={r.notification_preference} />
                    {r.last_emailed_at && (
                      <span className="text-[10px] text-green-600 flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" /> {timeAgo(r.last_emailed_at)}
                      </span>
                    )}
                  </div>
                </label>
              ))}
            </div>

            {/* Push message — shown when any selected worker gets a push */}
            {anyPush && (
              <div className="space-y-2 border-t pt-3">
                <div className="flex items-center justify-between gap-2">
                  <Label className="label-overline flex items-center gap-1.5">
                    <Smartphone className="h-3.5 w-3.5" /> Push message
                  </Label>
                  {templates.length > 0 && (
                    <Select
                      value={templateId}
                      onValueChange={(id) => { const t = templates.find((x) => x.id === id); if (t) applyTemplate(t); }}
                    >
                      <SelectTrigger className="h-8 w-40 text-xs" data-testid="notify-template-select">
                        <SelectValue placeholder="Template" />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map((t) => (
                          <SelectItem key={t.id} value={t.id} className="text-xs">{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <input
                  className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                  value={pushTitle}
                  onChange={(e) => setPushTitle(e.target.value)}
                  placeholder="Notification title"
                  data-testid="notify-push-title"
                />
                <Textarea
                  value={pushMessage}
                  onChange={(e) => setPushMessage(e.target.value)}
                  rows={3}
                  className="text-sm"
                  placeholder="Notification message"
                  data-testid="notify-push-message"
                />
                <p className="text-[10px] text-muted-foreground">Review and edit before sending. Email recipients get the standard job-details email.</p>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button className="btn-volt" onClick={handleSend} disabled={sending || chosen.length === 0} data-testid="send-notify-workers-btn">
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Bell className="h-4 w-4 mr-2" />}
            Notify{chosen.length ? ` (${chosen.length})` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NotifyWorkersDialog;
